/**
 * Shared date-formatting primitives.
 *
 * The codebase needs the same low-level operations in four places:
 *   - `engine/pivots/breakdown.ts`          — breakdown pivot keying
 *   - `engine/pivots/compare.ts`            — compare pivot keying
 *   - `widgets/chart-axis-config.ts`        — chart axis labels
 *   - `widgets/DataTable.tsx`               — table cell rendering
 *
 * Before R-11 each location carried its own `pad2` / `isoFromEpoch`
 * pair, with the categoryKey logic literally copy-pasted between
 * breakdown and compare. This module is the single source for those
 * primitives; the four callers compose them but keep their distinct
 * public APIs (`formatAxisLabel`, `formatDateLike`, …) intact.
 */

/**
 * Smallest epoch value (in ms) we treat as a "date" rather than a plain
 * numeric category. Roughly equivalent to 1973-03-03 — small enough to
 * cover any real dataset, large enough to leave plain numeric categories
 * (sale amounts, counts, tier ids) untouched.
 */
export const MIN_EPOCH_MS = 1e11;

/** Left-pad a number with a leading zero when it's < 10. */
export function pad2(n: number): string {
  return n < 10 ? '0' + n : String(n);
}

/** Format the date's UTC date part as DD.MM.YYYY. */
export function formatDDMMYYYY(date: Date): string {
  return `${pad2(date.getUTCDate())}.${pad2(date.getUTCMonth() + 1)}.${date.getUTCFullYear()}`;
}

/**
 * Convert a millisecond epoch to an ISO date string (YYYY-MM-DD) in UTC.
 * Returns null when the input yields an invalid date.
 */
export function isoFromEpoch(ms: number): string | null {
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/**
 * Stringify a category value for keying / display. Date instances and
 * epoch numbers / bigints are normalised to YYYY-MM-DD so two rows for
 * the same calendar day collide on the same key regardless of how
 * DuckDB returned the value (Date object, ms number, or bigint).
 *
 * Epoch numbers slip in when DuckDB's CASE-on-DATE result lands on the
 * JS side as a bigint→number through `executeQueryInner` (observed on
 * the compare-mode period-shift expression). The MIN_EPOCH_MS threshold
 * leaves plain numeric categories untouched.
 */
export function categoryKey(value: unknown): string {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const iso = isoFromEpoch(value.getTime());
    if (iso) return iso;
  }
  if (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value >= MIN_EPOCH_MS
  ) {
    const iso = isoFromEpoch(value);
    if (iso) return iso;
  }
  if (typeof value === 'bigint') {
    const n = Number(value);
    if (Number.isFinite(n) && n >= MIN_EPOCH_MS) {
      const iso = isoFromEpoch(n);
      if (iso) return iso;
    }
  }
  return String(value ?? '');
}
